import * as React from 'react';
import type { ICarouselProps } from '@coherence-design-system/carousel';
import { Carousel } from '@coherence-design-system/carousel';
import {
  Body1,
  Body1Strong,
  Card,
  CardHeader,
  CardPreview,
  Display,
  Image,
  makeStyles,
  Persona,
  shorthands,
  Subtitle1,
  Subtitle2,
  Title1,
  Title2,
  Title3,
  tokens,
} from '@fluentui/react-components';
import { Alert } from '@fluentui/react-components/unstable';
import loadable from '@loadable/component';
import { useMediaQuery } from '@react-hook/media-query';
import { defineMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { TextLink } from '@foundershub/components';
import { Oembed } from '@foundershub/oembed';
import { LinkedInButton } from '../../components';
import { CenteredLoadingSpinner } from '../../components/CenteredLoadingSpinner';
import { DrawerContext } from '../../components/FoundersHubUserOverlayDrawer';
import aiToolsWebp from '../../media/ai-tools.webp';
import eligibilityReqWebp from '../../media/card-checkmark.webp';
import circleSvg from '../../media/circle.svg';
import timelineWebp from '../../media/clock-progress.webp';
import connectorSvg from '../../media/connector.svg';
import devWebp from '../../media/dev.webp';
import deviceMultipleWebp from '../../media/device-multiple.webp';
import beeKeeper from '../../media/logos/BeeKeeperAI.svg';
import deepBrain from '../../media/logos/DeepBrainAI.svg';
import gytpolLogo from '../../media/logos/Gytpol.webp';
import gytpol from '../../media/logos/GytpolLogoAndText.svg';
import pangea from '../../media/logos/Pangaea.svg';
import perplexityLogo from '../../media/logos/PerplexityAILogo.svg';
import pinecone from '../../media/logos/pinecone.svg';
import silverfort from '../../media/logos/silverfort.svg';
import wAndB from '../../media/logos/WandB.svg';
import peopleSvg from '../../media/people.svg';
import workspaceWebp from '../../media/web-workspace.webp';
import { usePixelTracking } from '../../modules/telemetry/tags';

const SupportDrawer = loadable(() => import(/* webpackChunkName: "support-drawer" */ '../SupportForm/SupportDrawer'), {
  fallback: <CenteredLoadingSpinner />,
});

interface JoinReasonCardProps {
  image: string;
  title: string;
  description: string;
}

interface ApplyInfoCardProps {
  image: string;
  title: string;
  content: React.ReactNode;
}

export default function Signup() {
  const intl = useIntl();
  const location = useLocation();
  const classes = useStyles();
  const isSmall = useMediaQuery('screen and (max-width: 1023px)');
  const [errorMessage, setErrorMessage] = React.useState('');
  usePixelTracking();

  const urlParams = new URLSearchParams(location.search);
  const drawerOpen = urlParams.get('drawer_open');
  const { setShowDrawer, setChildren } = React.useContext(DrawerContext);

  React.useEffect(() => {
    if (drawerOpen === 'true') {
      setShowDrawer(true);
      setChildren(<SupportDrawer />);
    }
  }, [drawerOpen]);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get('error');
    if (error) {
      const msg =
        {
          'no-email': noEmailMessage,
        }[error] || defaultErrorMessage;
      setErrorMessage(intl.formatMessage(msg));
    }
  });

  const featureImage = (
    <div className={classes.contentImage}>
      <Image width="256" height="256" src={workspaceWebp} alt="" />
    </div>
  );

  const pageTitle = intl.formatMessage(heroTitle);
  const QuoteTextCompo = isSmall ? Title3 : Title2;

  const carouselProps: ICarouselProps = {
    title: intl.formatMessage(companyLogosLabel),
    responsive: true,
    items: [
      <Card key={'perplexity'}>
        <CardPreview>
          <Image src={perplexityLogo} alt="logo for startup perplexity" />
        </CardPreview>
      </Card>,
      <Card key={'pinecone'}>
        <CardPreview>
          <Image src={pinecone} alt="logo for startup pinecone" />
        </CardPreview>
      </Card>,
      <Card key={'wAnB'}>
        <CardPreview>
          <Image src={wAndB} alt="logo for startup w&b" />
        </CardPreview>
      </Card>,
      <Card key={'silverfort'}>
        <CardPreview>
          <Image src={silverfort} alt="logo for startup silverfort" />
        </CardPreview>
      </Card>,
      <Card key={'pangea'}>
        <CardPreview>
          <Image src={pangea} alt="logo for startup pangea" />
        </CardPreview>
      </Card>,
      <Card key={'gytpol'}>
        <CardPreview>
          <Image src={gytpol} alt="logo for startup gytpol" />
        </CardPreview>
      </Card>,
      <Card key={'deepBrain'}>
        <CardPreview>
          <Image src={deepBrain} alt="logo for startup deepbrain" />
        </CardPreview>
      </Card>,
      <Card key={'beeKeeper'}>
        <CardPreview>
          <Image src={beeKeeper} alt="logo for startup beekeeper" />
        </CardPreview>
      </Card>,
    ],
    styles: {
      container: {
        // This overrides the default space between the carousel items, allowing the box shadow to of all cards to show.
        'div[class*=coher-CardItem]': {
          padding: '12px',
        },
        // This overrides the default space between the carousel title and the carousel items
        'div[class^=headerContainer]': {
          marginBottom: '4px',
        },
        // This overrides the default carousel title styles to match the Subtitle1 styles
        'h2[class^=header-]': {
          fontSize: tokens.fontSizeBase500,
          fontWeight: tokens.fontWeightSemibold,
          lineHeight: tokens.lineHeightBase500,
          fontFamily: tokens.fontFamilyBase,
          overflow: 'auto',
          whiteSpace: 'normal',
        },
      },
    },
    breakpoints: {
      xxxl: { width: 1800, items: 4 },
      xxl: { width: 1440, items: 4 },
      xl: { width: 1024, items: 4 },
      l: { width: 640, items: 2 },
      m: { width: 480, items: 2 },
      s: { width: 320, items: 1 },
    },
  };

  return (
    <div className={classes.root}>
      <div className={classes.mainContainer}>
        <div className={classes.headingContent}>
          {isSmall && featureImage}
          <div>
            {isSmall ? <Title1>{pageTitle}</Title1> : <Display>{pageTitle}</Display>}
            <Body1 className={classes.introText}>{intl.formatMessage(intro)}</Body1>
            <div className={classes.applyButton}>
              <LinkedInButton loginUri="/auth/linkedin-login" text={intl.formatMessage(applyLabel)} />
            </div>
            {errorMessage && <Alert intent="error">{errorMessage}</Alert>}
          </div>
          {!isSmall && featureImage}
        </div>

        <Oembed url="https://youtu.be/MI3ujBQ5Rnc?si=0Ny-t5YOTXjgBaCv" loadingText={intl.formatMessage(loadingText)} />

        <Card className={classes.testimonyCard}>
          <QuoteTextCompo align="center" className={classes.quoteText}>
            {intl.formatMessage(testimonialQuote)}
          </QuoteTextCompo>
          <Persona
            avatar={{
              role: 'presentation',
              image: { alt: 'Gytpol logo', src: gytpolLogo },
              size: 40,
              color: 'colorful',
            }}
            primaryText={<Subtitle2>Tal Kollender</Subtitle2>}
            secondaryText={<Subtitle2>{intl.formatMessage(testifierDescription)}</Subtitle2>}
            textAlignment="center"
          />
        </Card>
        <div className={classes.cardSection}>
          <Subtitle1>{intl.formatMessage(whyJoinLabel)}</Subtitle1>
          <div className={classes.cards}>
            <JoinReasonCard
              image={aiToolsWebp}
              title={intl.formatMessage(aiCardTitle)}
              description={intl.formatMessage(aiCardDescription)}
            />
            <JoinReasonCard
              image={deviceMultipleWebp}
              title={intl.formatMessage(azureCardTitle)}
              description={intl.formatMessage(azureCardDescription)}
            />
            <JoinReasonCard
              image={peopleSvg}
              title={intl.formatMessage(expertsCardTitle)}
              description={intl.formatMessage(expertsCardDescription)}
            />
            <JoinReasonCard
              image={devWebp}
              title={intl.formatMessage(devToolsCardTitle)}
              description={intl.formatMessage(devToolsCardDescription)}
            />
          </div>
        </div>
        <div className={classes.cardSection}>
          <Subtitle1>{intl.formatMessage(applyInfoSectionLabel)}</Subtitle1>
          <div className={classes.cards}>
            <ApplyInfoCard
              image={eligibilityReqWebp}
              title={intl.formatMessage(eligibilityReqsLabel)}
              content={
                <Body1>
                  {intl.formatMessage(eligibilityReqsPreface)}
                  <ul>
                    <li>{intl.formatMessage(eligibilityReq1)}</li>
                    <li>{intl.formatMessage(eligibilityReq2)}</li>
                    <li>{intl.formatMessage(eligibilityReq3)}</li>
                    <li>{intl.formatMessage(eligibilityReq4)}</li>
                  </ul>
                  {intl.formatMessage(fullCriteriaLabel, {
                    link: function InlineLink(str) {
                      // @ts-ignore formatjs/formatjs#3707
                      return (
                        <TextLink
                          href="https://foundershubsupportcenter.powerappsportals.com/category/?id=CAT-01005"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {str}
                        </TextLink>
                      ) as string;
                    },
                  })}
                </Body1>
              }
            />
            <ApplyInfoCard
              image={timelineWebp}
              title={intl.formatMessage(applyTimelineTitle)}
              content={
                <div className={classes.applyTimeline}>
                  <span className={classes.timelineNode}>
                    <Image src={circleSvg} alt="timeline node" width={20} height={20} />
                    <Body1>{intl.formatMessage(applyTimelineLabel1)}</Body1>
                  </span>
                  <Image src={connectorSvg} alt="timeline connector" width={20} height={40} />
                  <span className={classes.timelineNode}>
                    <Image src={circleSvg} alt="timeline node" width={20} height={20} />
                    <Body1>{intl.formatMessage(applyTimelineLabel2)}</Body1>
                  </span>
                  <Image src={connectorSvg} alt="timeline connector" width={20} height={40} />
                  <span className={classes.timelineNode}>
                    <Image src={circleSvg} alt="timeline node" width={20} height={20} />
                    <Body1>{intl.formatMessage(applyTimelineLabel3)}</Body1>
                  </span>
                  <Image src={connectorSvg} alt="timeline connector" width={20} height={40} />
                  <span className={classes.timelineNode}>
                    <Image src={circleSvg} alt="timeline node" width={20} height={20} />
                    <Body1>{intl.formatMessage(applyTimelineLabel4)}</Body1>
                  </span>
                </div>
              }
            />
          </div>
        </div>
        <div className={classes.carouselContainer}>
          <Carousel {...carouselProps} />
        </div>
        <Body1 className={classes.terms}>
          {intl.formatMessage(termsText, {
            link: function InlineLink(str) {
              // @ts-ignore formatjs/formatjs#3707
              return (
                <TextLink href="https://go.microsoft.com/fwlink/?LinkId=521839" target="_blank" rel="noreferrer">
                  {str}
                </TextLink>
              ) as string;
            },
          })}
        </Body1>
      </div>
    </div>
  );
}

function JoinReasonCard({ image, title, description }: JoinReasonCardProps) {
  const classes = useStyles();
  return (
    <Card className={classes.joinReasonCard}>
      <CardHeader
        image={{ src: image, alt: title, as: 'img', width: 52, height: 53 }}
        header={<Body1Strong>{title}</Body1Strong>}
        className={classes.joinReasonCardHeader}
      />
      <Body1>{description}</Body1>
    </Card>
  );
}

function ApplyInfoCard({ image, title, content }: ApplyInfoCardProps) {
  const classes = useStyles();

  return (
    <Card className={classes.applyInfoCard}>
      <Image src={image} alt={title} className={classes.applyInfoCardImage} width={128} height={128} />
      <div className={classes.applyInfoCardBody}>
        <Subtitle1 block>{title}</Subtitle1>
        {content}
      </div>
    </Card>
  );
}

const loadingText = defineMessage({
  defaultMessage: 'Loading...',
  description: 'Label for loading spinner in video component.',
});
const heroTitle = defineMessage({
  defaultMessage: 'Accelerate your startup growth with AI',
  description: 'Sign up page hero title',
});
const intro = defineMessage({
  defaultMessage:
    'Microsoft for Startups Founders Hub provides free access to leading AI models through Azure, including OpenAI GPT-4, up to $150,000 in Azure credits, one-on-one guidance from Microsoft experts, and so much more. Open to anyone with an idea—no funding required.',
  description: 'Sign up page intro',
});
const applyLabel = defineMessage({
  defaultMessage: 'Apply with LinkedIn',
  description: 'apply with linkedin text',
});
const termsText = defineMessage({
  defaultMessage:
    'By submitting this application, you agree that you have read the <link>Microsoft Privacy Statement</link> and give permission to Microsoft for Startups to collect your data and contact you regarding your application.',
  description: 'privacy statement acknowledgement on submission of application text',
});
const defaultErrorMessage = defineMessage({
  defaultMessage:
    'There was an error authenticating you. Please try again in a few minutes. If this problem persists please contact support.',
  description: 'error message for users with expert login issues',
});
const noEmailMessage = defineMessage({
  defaultMessage:
    'Your account does not have an email address associated with it. Please update your account to include an email address and try again.',
  description: 'error message for users without email address',
});
const testimonialQuote = defineMessage({
  defaultMessage: '“No other cloud provider comes close to what Microsoft offers startups.”',
  description: 'quote/testimonial/review from a product user',
});
const testifierDescription = defineMessage({
  defaultMessage: 'CEO and Co-Founder, Gytpol',
  description: '{Locked="Gytpol"}, testifier description',
});
const whyJoinLabel = defineMessage({
  defaultMessage: 'Why join Microsoft for Startups Founders Hub?',
  description: '{Locked="Microsoft for Startups Founders Hub"}, question that titles the section',
});
const aiCardTitle = defineMessage({
  defaultMessage: 'Cutting-edge AI tools',
  description: 'title of card about artificial intelligence-related offerings',
});
const aiCardDescription = defineMessage({
  defaultMessage:
    'All startups get free access to leading AI models through Azure, including OpenAI GPT-4, Llama 2 from Meta, and more. Get up to $150,000 in Azure credits, plus $2,500 of OpenAI credits.',
  description: '{Locked="OpenAI", "GPT-4", "Azure"}, description of Founders Hub/Azure offering',
});
const azureCardTitle = defineMessage({
  defaultMessage: 'Free Azure credits',
  description: 'title of card about Founders Hub offering',
});
const azureCardDescription = defineMessage({
  defaultMessage: 'Up to $150,000 in Azure credits to conserve runway while you experiment, prototype, and build.',
  description: '{Locked="Azure"}, description of Founders Hub Azure offering',
});
const expertsCardTitle = defineMessage({
  defaultMessage: 'Access to real people',
  description: 'title of card about Founders Hub offering',
});
const expertsCardDescription = defineMessage({
  defaultMessage:
    'Unlimited 1:1 meetings with experts who can help solve immediate business challenges, plus provide technical guidance on the latest in AI.',
  description: 'description of Founders Hub offering',
});
const devToolsCardTitle = defineMessage({
  defaultMessage: 'Free software and development tools',
  description: 'title of card about Founders Hub offering',
});
const devToolsCardDescription = defineMessage({
  defaultMessage:
    '30+ additional free and discounted tools, tech, and services from Microsoft and our partners including M365, GitHub, LinkedIn, and more.',
  description: 'description of Founders Hub offering',
});
const applyInfoSectionLabel = defineMessage({
  defaultMessage: 'Applying is fast and easy',
  description: 'title of section',
});
const eligibilityReqsLabel = defineMessage({
  defaultMessage: 'Eligibility requirements',
  description: 'title of card',
});
const eligibilityReqsPreface = defineMessage({
  defaultMessage:
    'Microsoft for Startups Founders Hub is open to anyone – from students with an idea to serial founders. However, to be eligible for the program, your startup must be:',
  description: 'preface to a list of eligibility requirements',
});
const eligibilityReq1 = defineMessage({
  defaultMessage: 'Building a software based product or service',
  description: 'eligibility requirement',
});
const eligibilityReq2 = defineMessage({
  defaultMessage: 'Privately held and for-profit',
  description: 'eligibility requirement',
});
const eligibilityReq3 = defineMessage({
  defaultMessage: 'Have not received Series D or later funding',
  description: 'eligibility requirement',
});
const eligibilityReq4 = defineMessage({
  defaultMessage: 'Have not previously received more than $10,000 in Azure credits',
  description: 'eligibility requirement',
});
const fullCriteriaLabel = defineMessage({
  defaultMessage: 'Full criteria <link>here</link>.',
  description: 'link to full eligibility criteria',
});
const applyTimelineTitle = defineMessage({
  defaultMessage: 'Application timeline',
  description: 'timeline title',
});
const applyTimelineLabel1 = defineMessage({
  defaultMessage: 'Application submitted',
  description: 'timeline node label',
});
const applyTimelineLabel2 = defineMessage({
  defaultMessage: 'Business credentials verified',
  description: 'timeline node label',
});
const applyTimelineLabel3 = defineMessage({
  defaultMessage: 'Applications typically processed in 3 business days',
  description: 'timeline node label that describes time window for processing applications',
});
const applyTimelineLabel4 = defineMessage({
  defaultMessage: 'Final decision sent to your email',
  description: 'timeline node label',
});
const companyLogosLabel = defineMessage({
  defaultMessage: 'Discover the startups we’re helping scale to the next level',
  description: 'Title of section that displays logos of companies that are part of the program',
});

const useStyles = makeStyles({
  root: {
    width: '100vw',
    height: '100%',
    display: 'flex',
    ...shorthands.flex(1),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: 'calc(100vw - 32px)',
    maxWidth: '1076px',
    ...shorthands.gap('48px'),
    ...shorthands.margin('60px', 'auto'),
    '@media screen and (max-width: 1023px)': {
      ...shorthands.gap('24px'),
      ...shorthands.margin('24px', '16px'),
    },
  },
  headingContent: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('105px'),
    '@media screen and (max-width: 1023px)': {
      flexDirection: 'column-reverse',
      ...shorthands.gap('16px'),
    },
    '@media screen and (max-width: 639px)': {
      flexDirection: 'column',
    },
  },
  introText: {
    display: 'flex',
    maxWidth: '560px',
    marginTop: tokens.spacingVerticalM,
    '@media screen and (max-width: 1023px)': {
      maxWidth: '100%',
    },
  },
  applyButton: {
    marginTop: `calc(${tokens.spacingVerticalXXXL} + ${tokens.spacingVerticalXS})`,
    '@media screen and (max-width: 1023px)': {
      marginTop: tokens.spacingVerticalXXL,
    },
  },
  contentImage: {
    ...shorthands.padding('0', '0'),
  },
  testimonyCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingVerticalM),
    ...shorthands.padding(tokens.spacingVerticalL, 0),
    backgroundImage:
      'linear-gradient(91deg, rgba(226, 80, 250, 0.20) 5.56%, rgba(5, 121, 228, 0.20) 50.66%, rgba(76, 5, 228, 0.20) 96.7%)',
  },
  quoteText: {
    display: 'flex',
    ...shorthands.padding(tokens.spacingHorizontalM),
  },
  cardSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalL),
  },
  cards: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
    ...shorthands.gap(tokens.spacingHorizontalXXL),
    '@media screen and (max-width: 1023px)': {
      gridAutoFlow: 'row',
      gridAutoRows: '1fr',
    },
  },
  carouselContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  joinReasonCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    ...shorthands.flex(1),
    ...shorthands.padding(tokens.spacingVerticalL),
  },
  joinReasonCardHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  applyInfoCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    ...shorthands.flex(1),
    ...shorthands.padding('36px', tokens.spacingHorizontalL),
    ...shorthands.gap(tokens.spacingHorizontalL),
    '@media screen and (max-width: 1023px)': {
      flexDirection: 'column',
      ...shorthands.padding(tokens.spacingHorizontalL),
    },
  },
  applyInfoCardBody: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  applyInfoCardImage: {
    maxWidth: '128px',
    maxHeight: '128px',
  },
  applyTimeline: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
  },
  timelineNode: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
  },
  terms: {
    width: '100%',
    '@media screen and (max-width: 1023px)': {
      width: '100%',
    },
  },
});
